import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators';
import { Vue } from 'vue-property-decorator';
import axios from 'axios';
import { API_URL } from '@/App.settings';
import moment from 'moment';
import FormDraft from '@/types/fields/FormDraft';
import SubFormDraft from '@/types/fields/SubFormDraft';
import VersionHistory from '@/types/VersionHistory';
import Dictionary from '@/types/Dictionary';
import Contact from '@/types/Contact';
import SaveModeEnum from '@/enums/SaveModeEnum';
import Section from '@/types/fields/Section';
import FormField from '@/types/fields/FormField';
import TableField from '@/types/fields/TableField';
import FieldTypeEnum from "@/enums/fields/FieldTypeEnum";
import TextField from '@/types/fields/TextField';
import NumberField from '@/types/fields/NumberField';
import IFormElement from '@/interfaces/IFormElement';
import IFormField from '@/interfaces/IFormField';
import IField from '@/interfaces/IField';
import ISection from '@/interfaces/ISection';
import Period from '@/types/Period';
import { i18n } from '@/main';
import TreeNode from '@/types/TreeNode';
import { StoreMediator } from '@/mediators/StoreMediator';

/** 
 * Store de gestion de la fiche objet (composant ObjectDisplaying et autres composants en dépendant) 
 * Ce store est utilisable uniquement en préfixant avec le namespace ObjectStore/ (car namespaced: true)
 */
@Module({
    namespaced: true
})
class ObjectStore extends VuexModule {
//#region Data
    /**  Liste des formulaires */
    private forms: FormDraft[] = [];
    private versionsHistory: VersionHistory[] = [];
    private isSaveHidden: boolean = false;
    private isVersionsHidden: boolean = false;
    /** Id de la version en cours */
    private versionId: number = 0;
    private defaultFormName: string = "Pictures";

    /** Indique si des valeurs ont été changés */
    private updatedForms: string[] = [];

    /** Erreurs associées à un champ lors de la validation des formulaires */
    private formsErrors: Dictionary<{ Target: string, FormLabel: string, IsFullWidth: boolean, FieldsErrors: Dictionary<string> }> = {};

    /** 
     * Compteur d'objet temporaire utilisé avant que l'objet ne soit effectivement sauvegardé côté back server
     * Il comporte des Id négatifs décrémentés pour chaque nouvel objet temporaire géré
     */
    private tempObjectId: number = 0;

    //#region Noeud ouvert dans la fiche objet
    private nodeId: string = "";

    /** Indique si le formulaire objet doit être rechargé */
    private refreshKey: number = 0;

    /** Mode de sauvegarde à utiliser sur Save() lorsqu'il doit être défini au préalable (sur New/création d'un objet par exemple) */
    private saveMode: SaveModeEnum = SaveModeEnum.UpdateObject;
    //#endregion Noeud ouvert dans la fiche objet

    //#region Pour nouvel objet
    /** Type d'objet de la fiche en cours */
    private objectType: string = "";

    /** Noeud parent pour la création d'un nouvel objet */
    private parentNode: TreeNode = {} as TreeNode;

    //#endregion Pour nouvel objet

    //#region Gestion du copier-coller
    /** Id de noeud copié */
    private copiedNodeId: string = "";
    //#endregion Gestion du copier-coller
//#endregion Data
//#region Mutations
    /** Réinitalisation du store VueX */
    @Mutation
    private reset() {
        this.forms = [];
        this.versionsHistory = [];
        this.isSaveHidden = false;
        this.isVersionsHidden = false;
        this.versionId = 0;
        this.defaultFormName = "Pictures";
        this.updatedForms = [];
        this.formsErrors = {};
        this.tempObjectId = 0;
        this.nodeId = "";
        this.objectType = "";
        this.parentNode = {} as TreeNode;
        this.refreshKey = 0;
        this.saveMode = SaveModeEnum.UpdateObject;
        this.copiedNodeId = "";
    }

    @Mutation
    private setNode({ nodeId, objectType }) {
        this.nodeId = nodeId;
        if (objectType)	// nodeObjectType facultatif car il peut déjà avoir été affecté après un New. Seul le nodeId est alors affecté.
            this.objectType = objectType;
    }

    /** Renseigne le NodeId parent dans le cas de la création d'un nouvel SaveModeEnum = SaveModeEnum.NewObject */
    @Mutation
    private setNewObject({ objectType, parentNode }) {
        this.parentNode = parentNode;
        this.objectType = objectType;
    }

    @Mutation
    private setReload() {
        ++this.refreshKey;
    }

    @Mutation
    private setData({ versionId = 0, isSaveHidden = false, isVersionsHidden = false, forms = [], defaultFormName = "Pictures" }) {
        console.debug("ObjectStore - setData({ versionId, forms }) - versionId : %O - isSaveHidden : %O - isVersionsHidden : %O", versionId, isSaveHidden, isVersionsHidden);

        this.versionId = versionId;
        this.isSaveHidden = isSaveHidden;
        this.isVersionsHidden = isVersionsHidden;
        this.forms = forms;
        this.defaultFormName = defaultFormName;

        // Réinitilisation des formulaires mis à jour
        this.updatedForms = [];
        // Réinitilisation des formulaires en erreur
        this.formsErrors = {};
    }

    /** Réinitialise la liste des formulaires qui ont été modifiés */
    @Mutation
    private cleanUpdatedForm() {
        this.updatedForms = [];
    }

    @Mutation
    private initFieldsError() {
        this.formsErrors = {};
    }

    @Mutation
    private setFieldError({ formName, target, fieldName, formLabel, isFullWidth, error }) {
        /** Si le formulaire n'existe pas déjà dans le dico alors on l'ajout */
        if (!(formName in this.formsErrors))
            Vue.set(this.formsErrors, formName,  { Target: target, FormLabel: formLabel, IsFullWidth: isFullWidth, FieldsErrors: {} });

        /** Ajout du champ dans la liste des erreurs */
        Vue.set(this.formsErrors[formName]['FieldsErrors'], fieldName,  error);
    }

    @Mutation
    private setVersionsHistory(data: VersionHistory[]) {
        this.versionsHistory = data;
    }

    /** Décrémente le compteur d'objet temporaire */
    @Mutation
    private setTempObjectId() {
        --this.tempObjectId;
    }

    /** Applique un mode de sauvegarde de l'objet + type d'objet lorsque la méthode Save() sera appelée */
    @Mutation
    private setSaveMode(saveMode: SaveModeEnum) {
        this.saveMode = saveMode;
    }

    //#region Gestion du copier-coller
    @Mutation
    private copy(nodeId) {
        this.copiedNodeId = nodeId;
    }
    //#endregion Gestion du copier-coller

    /** Déprécie/apprécie l'ensemble des lignes de tableau enfants */
    @Mutation
    private depreciateTableFieldRows({ depreciate }) {
        let idChildForm: number = this.forms.findIndex(x => x.Name == "Children");
        if (idChildForm > -1) {
            let childForm: FormDraft = this.forms[idChildForm];
            console.debug("ObjectStore - depreciateTableFieldRows()");

            // Recherche les rows avec un nodeId dans toutes les sections, tous les champs de type TableField
            for (let section of childForm.Sections) {
                for (let field of (section as Section).Fields) /** @todo : trouver comment faire un find plutôt que le if en dessous....🙄 */
                    if ((field as FormField).Type == FieldTypeEnum.Table) {
                        for (let row of (field as TableField).Value) {
                            row.isDepreciated = depreciate;
                            console.debug("ObjectStore - depreciateTableFieldRows() - row.isDepreciated : %O", row.isDepreciated);
                        }
                    }
            }
        }
    }

    /** Renomme un objet */
    @Mutation
    private rename({ nodeId, newName }) {
        // Si le noeud renommé est le même que celui de la fiche objet alors on met à jour le nom sans repasser par le server
        if (this.nodeId == nodeId && this.forms) {
            let idFormIdentity: number = this.forms.findIndex(x => x.Name === "Identity");
            if (idFormIdentity > -1) {
                let form: FormDraft = this.forms[idFormIdentity];
                let idSectionGenInfo: number = form.Sections.findIndex(x => x.Name === "GeneralInformation");
                if (idSectionGenInfo > -1) {
                    let section: ISection = form.Sections[idSectionGenInfo];
                    let idField: number = section.Fields.findIndex(x => (x as IFormField).Name == "Name");
                    if (idField > -1) {
                        (section.Fields[idField] as TextField).Value = newName;
                    }
                }
            }
        }
    }

    /** 
     * Mise à jour valeur d'un field
     * @param newElement: champ à jour
     *  */
    @Mutation
    private updateValue({ newElement }) {
        let element: IFormElement = getFormElement(this.forms, newElement.Name);
        console.debug("ObjectStore - updateValue() - newElement : %O - element : %O", newElement, element);

        if (element["Transmitters"]) {
            for (let transmitter of element["Transmitters"]) {
                // Récupère le Field cible
                let target: IFormElement = getFormElement(this.forms, transmitter.Name);
                let splitT: string[] = transmitter.Target.split(".");
                let t: string = (splitT) ? splitT[splitT.length - 1] : "";
                splitT.pop();
                for (let strT of splitT) {
                    target = target[strT];
                }

                // Récupère le field source
                let source: IFormElement = element;
                let splitS: string[] = transmitter.Source.split(".");
                let s: string = (splitS) ? splitS[splitS.length - 1] : "";
                splitS.pop();
                for (let strS of splitS) {
                    source = source["Extras"];
                }

                let check: boolean = true;

                switch (transmitter.Sign) {
                    case "=":
                        if(source[s] == transmitter.Value){
                            if (transmitter.TrueValue != undefined)
                                target[t] = transmitter.TrueValue;
                        } else {
                            if (transmitter.FalseValue != undefined)
                                target[t] = transmitter.FalseValue;
                        }
                        console.debug("ObjectStore - updateValue() - case '=' - source : %O - transmitter : %O", source, transmitter);
                        break;
                    // Todo : tester le switch
                    case "!":
                        if(source[s] != transmitter.Value){
                            if (transmitter.TrueValue != undefined)
                                target[t] = transmitter.TrueValue;
                        } else {
                            if (transmitter.FalseValue != undefined)
                                target[t] = transmitter.FalseValue;
                        }
                        break;
                    // TODO : voir avec SLavina pour ce que contiendra le switch
                    //case "&":
                    //	target[t] = (source[s] && transmitter.Value);
                    //	break;
                    //case "|":
                    //	target[t] = (source[s] || transmitter.Value);
                    //	break;
                    //case "<":
                    //	target[t] = (source[s] =< transmitter.Value);
                    //	break;
                    case ">":
                        console.debug("ObjectStore - updateValue() - case > - source : %O", source);
                        console.debug("ObjectStore - updateValue() - case > !! : target[t] : %O - source[s] : %O - transmitter.Value : %O - source[transmitter.Value] : %O", target[t], source[s], transmitter.Value, source[transmitter.Value]);
                        target[t] = (check && transmitter.DefaultValue) ? transmitter.DefaultValue : source[s];
                        if(source[s] >= transmitter.Value){
                            if (transmitter.TrueValue != undefined)
                                target[t] = transmitter.TrueValue;
                        } else {
                            if (transmitter.FalseValue != undefined)
                                target[t] = transmitter.FalseValue;
                        }
                        break;
                    //case "*":
                    //	target[t] = (source[s] * transmitter.Value);
                    //	break;
                    //case "/":
                    //	target[t] = (source[s] / transmitter.Value);
                    //	break;
                    //case "-":
                    //	target[t] = (source[s] - transmitter.Value);
                    //	break;
                    //case "+":
                    //	target[t] = (source[s] + transmitter.Value);
                    //	break;
                    default:
                        if (s) {
                            target[t] = source[s];
                            if (t == "FilterValue") target["Value"] = 0;
                            console.debug("ObjectStore - updateValue() - case default - target.Name : %O - s : %O - source[s] : %O - target[t] : %O", target.Name, s, source[s], target[t]);
                        }
                        else {
                            target[t] = transmitter.Value;
                            console.debug("ObjectStore - updateValue() - case default - transmitter.Value : ", transmitter.Value);
                        }
                        break;
                }
                console.debug("ObjectStore - updateValue()- Name : %O - : Property : %O - Value : %O", transmitter.Name, t, target[t]);
            }
        }
    }

    @Mutation
    private updateField({ fieldName, property, value }) {
        console.debug('ObjectStore - updateField() - fieldName : %O - property : %O - value : %O', fieldName, property, value);
        let element: IFormElement = getFormElement(this.forms, fieldName);
        if (element)
            Vue.set(element, property, value);
    }

    /** 
     * Met à jour la liste des form mises à jour
     * @param formName Nom de la form a ajouter
     */
    @Mutation
    private addUpdatedForm(formName) {
        if (!this.updatedForms.includes(formName))
            this.updatedForms.push(formName);
    }

    /** Mise à jour d'un widget sur un tableau de bord (Pourquoi utiliser ça dans la fiche objet ?)
     * @param dashboardId Id du tableau de bord
     * @param widgetId  Identifiant du widget
     * @param datas Données du widget à mettre à jour
     **/
    @Mutation
    private updateWidget({ dashboardId, widgetId, datas }: { dashboardId: number, widgetId: number, datas: any }) {
        console.debug("ObjectStore - updateWidget - dashboardId : %O - widgetId : %O - datas : %O", dashboardId, widgetId, datas);

        let targetDashboardValue = null;

        // On cherche dans tous les formulaires (onglets) la section nommée "ObjectDashboard" dont l'Id du dashboard correspond
        for (const form of this.forms) {
            if (!form.Sections) continue;

            const section = form.Sections.find(s => 
                s.Name === "ObjectDashboard" && s.Value && s.Value.Id == dashboardId
            );

            if (section) {
                targetDashboardValue = section.Value;
                break; // On a trouvé le bon dashboard, on sort de la boucle
            }
        }

        // Si on n'a pas trouvé le dashboard
        if (!targetDashboardValue || !targetDashboardValue.Widgets) {
            console.warn(`ObjectStore - updateWidget: Dashboard avec l'Id ${dashboardId} introuvable.`);
            return;
        }

        /** Recherche du widget */
        const indexWidget: number = targetDashboardValue.Widgets.findIndex(x => x.Id == widgetId);

        if (indexWidget > -1) {
            /** Mise à jour du widget
             * Pour chaque paire clé-valeur, on met à jour la propriété sur le widget de manière réactive
             */
            for(let key in datas) {
                Vue.set(targetDashboardValue.Widgets[indexWidget], key, datas[key]);
            }
            
            // console.debug("ObjectStore - updateWidget() - Widget mis à jour: ", targetDashboardValue.Widgets[indexWidget]);
        } else
            console.warn(`ObjectStore - updateWidget: Widget avec l'Id ${widgetId} introuvable.`);
    }
//#endregion Mutations
//#region Actions
    /** Réinitalisation du store VueX */
    @Action({ rawError: true })
    public Reset() {
        this.context.commit('reset');
    }

    /** 
     * Met à jour la liste des form mises à jour
     * @param formName Nom de la form a ajouter
     */
    @Action({ rawError: true })
    public AddUpdatedForm(formName) {
        this.context.commit('addUpdatedForm', formName);
    }

    @Action({ rawError: true })
    public UpdateValue({ newElement }) {
        console.debug("ObjectStore - UpdateValue() - newElement : %O", newElement);
        this.context.commit('updateValue', { newElement: newElement });
    }

    @Action({ rawError: true })
    public SetNode({ node }) {
        this.context.commit('setSaveMode', SaveModeEnum.UpdateObject);
        let reload: boolean = this.nodeId === node?.Id;

        let nodeId: string = ''
        let objectType: string = '';
        if (node && Object.keys(node).length > 0) {
            nodeId = node.Id
            objectType = node.ObjectType;
        }
        this.context.commit('setNode', { nodeId: nodeId, objectType: objectType });
        if (reload)
            this.context.commit('setReload');
    }

    @Action({ rawError: true })
    public Reload() {
        this.context.commit('setReload');
    }

    /** 
     * Création d'un nouvel objet
     * @param objectType        Type du nouvel objet à créer
     * @param parentNode      NodeId du noeud parent dont doit dépendre le nouvel objet
     *  */
    @Action({ rawError: true })
    public async New({ objectType, parentNode}) {
        this.context.commit('setNewObject', { objectType: objectType, parentNode: parentNode });

        const args: Dictionary<any> = {};
        args["Token"] = Vue.cookies.get('access_token') != null ? <string>Vue.cookies.get('access_token') : "";

        await axios.post(API_URL + "object/" + objectType + "/New", args).then((response) => {
            if (response && response.data) {
                let loadedData: Dictionary<any> = response.data;

                console.debug("ObjectStore - New() - saveMode - avant les commit !!! ");
                this.context.commit('setSaveMode', SaveModeEnum.NewObject);
                this.context.commit('setNode', { nodeId: '', objectType: objectType });

                // Liste des formulaires pour le type objet
                this.context.commit('setData', { forms: loadedData["Forms"], defaultFormName: loadedData["DefaultFormName"] });

                /** Déclenche la mise à jour dans les autres stores Vuex */
                StoreMediator.GetInstance().NewObject(parentNode.ObjectType);

                console.debug("ObjectStore - New() - saveMode - après les commit !!! ");
            }
        });
    }

    /**
     * Création d'un nouvel équipement fantôme
     * @param objectType Type d'objet
     * @param parentNodeId Identifiant du noeud parent où on veut ajouter l'objet
     * @param selectedEquipment Identifiant de l'équipement à ajouter au parent
     */
    @Action({rawError : true})
    public async NewGhostEquipment({objectType, parentNodeId, selectedEquipment}){
        const args :Dictionary<any> = {};
        args["Token"] = Vue.cookies.get('access_token') != null ? <string>Vue.cookies.get('access_token') : "";
        args["ParentId"] = parentNodeId;
        args["EquipmentId"] = selectedEquipment;

        await axios.post(API_URL + "object/" + objectType + "/NewGhost", args).then((response) => {
            if(response && response.data){
                // Données chargées du type d'équipement (liste des formulaires + nom formulaire)
                let loadedData: Dictionary<any> = response.data;

                // On passe le mode de sauvegarde en mode mise à jour d'objet
                this.context.commit('setSaveMode', SaveModeEnum.UpdateObject);
                this.context.commit('setNode', { nodeId: loadedData["GhostId"], objectType: objectType });

                // Chargement des formulaires pour le type objet
                this.context.commit('setData', { forms: loadedData["Forms"], defaultFormName: loadedData["DefaultFormName"] });

                StoreMediator.GetInstance().NewGhostEquipment(loadedData["GhostId"]);
            }
        });
    }

    /** Récupération des informations liées à un objet */
    @Action({ rawError: true })
    public async LoadData() {
        /** Réinitialise la fiche objet pour décharger les formulaires si une fiche objet a déjà été ouverte */
        this.context.commit('setData', { });

        const args: Dictionary<any> = {};
        args["Token"] = Vue.cookies.get('access_token') != null ? <string>Vue.cookies.get('access_token') : "";
        args["ObjectId"] = this.nodeId;

        // Interroge l'API correspondant au type d'objet du SingleNode sélectionné
        await axios.post(API_URL + "object/" + this.objectType + "/Load", args).then((response) => {
            if (response && response.data) {
                let loadedData: Dictionary<any> = response.data;
                this.context.commit('setData', { versionId: loadedData["VersionId"], isSaveHidden: ("IsSaveHidden" in loadedData ? loadedData["IsSaveHidden"] : false), isVersionsHidden: ("IsVersionsHidden" in loadedData ? loadedData["IsVersionsHidden"] : false), forms: loadedData["Forms"], defaultFormName: loadedData["DefaultFormName"] });
                this.context.commit('setVersionsHistory', loadedData["Revisions"]);
                this.context.commit('setSaveMode', SaveModeEnum.UpdateObject);
            }
        });
    }

    @Action({ rawError: true })
    public async LoadVersion({ versionId }) {
        const args: Dictionary<any> = {};
        args["Token"] = Vue.cookies.get('access_token') != null ? <string>Vue.cookies.get('access_token') : "";
        args["ObjectId"] = this.nodeId;
        args["VersionId"] = versionId;

        // Interroge l'API correspondant au type d'objet du SingleNode sélectionné
        await axios.post(API_URL + "object/" + this.objectType + "/LoadVersion", args).then((response) => {
            if (response && response.data) {	// HACK: Utilisation d'une variable locale sinon pas possible de passer directement response.data au commit suivant.
                this.context.commit('setData', { versionId: versionId, forms: response.data });
                this.context.commit('setSaveMode', SaveModeEnum.UpdateVersion);
            }
        });
    }

    @Action({ rawError: true })
    public async GetRevisions() {
        const args: Dictionary<any> = {};
        args["Token"] = Vue.cookies.get('access_token') != null ? <string>Vue.cookies.get('access_token') : "";
        args["ObjectId"] = this.nodeId;

        // Interroge l'API correspondant au type d'objet du SingleNode sélectionné
        await axios.post(API_URL + "object/" + this.objectType + "/GetRevisions", args).then((response) => {
            if (response && response.data)
                this.context.commit('setVersionsHistory', response.data);
        }).catch(error => {
            this.context.commit('setVersionsHistory', []);
            throw(error);
        });
    }

    @Action({ rawError: true })
    public async DeleteVersion({ versionId }) {
        const args: Dictionary<any> = {};
        args["Token"] = Vue.cookies.get('access_token') != null ? <string>Vue.cookies.get('access_token') : "";
        args["ObjectId"] = this.nodeId;
        args["VersionId"] = versionId;

        // Interroge l'API correspondant au type d'objet du SingleNode sélectionné
        await axios.post(API_URL + "object/" + this.objectType + "/DeleteVersion", args).then(async () => {
            // Nécessité de recharger la liste des versions car les Ids ne sont plus les mêmes côté back...
            await this.context.dispatch("GetRevisions").then(() => {
                console.debug("ObjectStore - DeleteVersion() - GetRevisions succeed - this.versionsHistory.length : %O", this.versionsHistory?.length);
            });
        });
    }

    /**
     * Suppression d'un objet
     * @returns Une promise
     */
    @Action({ rawError: true })
    public async Delete({ nodeId, selectParentNode, childSectionToDelete = "", tableFieldNameToDelete = "" }) {
        const args: Dictionary<any> = {};
        args["Token"] = Vue.cookies.get('access_token') != null ? <string>Vue.cookies.get('access_token') : "";
        args["ObjectId"] = nodeId;

        await axios.post(API_URL + "object/Delete", args).then(async (response) => {
            if (response) {
                await StoreMediator.GetInstance().DeleteObject(nodeId, selectParentNode);

                if (childSectionToDelete != "" && tableFieldNameToDelete != "") {
                    // Suppression du noeud dans tableau enfants chargés dans forms
                    let idChildForm: number = this.forms.findIndex(x => x.Name == "Children");
                    if (idChildForm > -1) {
                        let childForm: FormDraft = this.forms[idChildForm];

                        // Recherche de la section correspondant au tableau
                        let idSection: number = childForm.Sections.findIndex(x => x.Name == childSectionToDelete);
                        if (idSection > -1) {
                            let section: ISection = childForm.Sections[idSection];

                            let idTableField: number = section.Fields.findIndex(x => (x as IFormField).Name == tableFieldNameToDelete);
                            if (idTableField > -1) {
                                let tableField: TableField = (section.Fields[idTableField] as TableField);

                                // Recherche de la ligne à supprimer
                                let idRow: number = tableField.Value.findIndex(x => x.nodeId == nodeId);
                                console.debug("ObjectStore - Delete() - childSectionToDelete : %O - tableFieldNameToDelete : %O - idRow to Delete : %O", childSectionToDelete, tableFieldNameToDelete, idRow);
                                if (idRow > -1) {
                                    tableField.Value.splice(idRow, 1);
                                    console.debug("ObjectStore - Delete() - childSectionToDelete : %O - tableFieldNameToDelete : %O - idRow to Delete %O supprimé !!", childSectionToDelete, tableFieldNameToDelete, idRow);
                                }
                            }
                        }
                    }
                }
            }
        });
    }

    @Action({ rawError: true })
    public async Appreciate({ nodeId, objectType, childSectionToAppreciate = "", tableFieldNameToAppreciate = "" }) {
        const args: Dictionary<any> = {};
        args["Token"] = Vue.cookies.get('access_token') != null ? <string>Vue.cookies.get('access_token') : "";
        args["ObjectId"] = nodeId;

        await axios.post(API_URL + "object/Appreciate", args).then((response) => {
            if (response) {
                // Recherche de la ligne à déprécier dans les rows du TableField du formulaire enfant
                if (childSectionToAppreciate != "" && tableFieldNameToAppreciate !== "") {
                    let rowDic: Dictionary<any> = getTableFieldRows(this.forms, "Children", childSectionToAppreciate, tableFieldNameToAppreciate, nodeId);
                    if (rowDic) {
                        let table: TableField = rowDic["tableRows"];
                        let idRow: string = rowDic["idRow"];

                        // Appréciation de la ligne
                        table[idRow].isDepreciated = false;
                    }
                }
                else {
                    // Appréciation des lignes des tableaux enfants
                    this.context.commit('depreciateTableFieldRows', { depreciate: false });
                }

                StoreMediator.GetInstance().AppreciateObject(nodeId);
            }
        });
    }

    @Action({ rawError: true })
    public async Depreciate({ nodeId, objectType, depreciatedDate, childSectionToDepreciate = "", tableFieldNameToDepreciate = "" }) {
        const args: Dictionary<any> = {};
        args["Token"] = Vue.cookies.get('access_token') != null ? <string>Vue.cookies.get('access_token') : "";
        args["ObjectId"] = nodeId;
        args["DepreciatedDate"] = moment(depreciatedDate).format('DD/MM/YYYY');

        await axios.post(API_URL + "object/Depreciate", args).then((response) => {
            if (response && response.data) {
                let success: boolean = response.data["Success"];

                if (success) {
                    let nodeIdToOpen: string = undefined;

                    // Recherche de la ligne à déprécier dans les rows du TableField du formulaire enfant
                    if (childSectionToDepreciate != "" && tableFieldNameToDepreciate !== "") {
                        let rowDic: Dictionary<any> = getTableFieldRows(this.forms, "Children", childSectionToDepreciate, tableFieldNameToDepreciate, nodeId);
                        if (rowDic) {
                            let table: TableField = rowDic["tableRows"];
                            let idRow: string = rowDic["idRow"];

                            // Dépréciation de la ligne dans tableau Enfants
                            table[idRow].isDepreciated = true;
                        }

                        /** Défini le noeud ouvert dans la fiche objet comme noeud à sélectionner dans l'arborescence après rafraichissement */
                        nodeIdToOpen = this.nodeId;
                    }
                    else {
                        // Dépréciation des lignes des tableaux enfants
                        this.context.commit('depreciateTableFieldRows', { depreciate: true });

                        /** Défini le noeud déprécié comme noeud à sélectionner dans l'arborescence après rafraichissement */
                        nodeIdToOpen = nodeId;
                    }

                    StoreMediator.GetInstance().DepreciateObject(nodeIdToOpen);
                }
                else {
                    let errorMessage: string = response.data["ErrorMessage"];
                    throw(new Error(errorMessage));
                }
            }
        });
    }

    @Action({ rawError: true })
    public async Duplicate({ nodeId }) {
        const args: Dictionary<any> = {};
        args["Token"] = Vue.cookies.get('access_token') != null ? <string>Vue.cookies.get('access_token') : "";
        args["ObjectId"] = nodeId;

        await axios.post(API_URL + "object/Duplicate", args).then((response) => {
            if (response) {
                StoreMediator.GetInstance().DuplicateObject(nodeId);
            }
        });
    }

    /** Réinitialise la liste des formulaires qui ont été modifiés */
    @Action({ rawError: true })
    public CleanUpdatedForm() {
        this.context.commit('cleanUpdatedForm');
    }

    /**
     * Enregistre les données de formulaire
     * @param localSaveMode Valeur de l'enum SaveModeEnum indiquant le mode de sauvegarde
     * @param versionDate Date de la version à créer pour le saveMode SaveModeEnum.NewVersion
     * @returns 
     */
    @Action({ rawError: true })
    public async Save({ forms, localSaveMode = undefined, versionDate = undefined }) {
        // Réinitialise le dictionnaire d'erreurs
        this.context.commit('initFieldsError');

        console.debug("ObjectStore - Save - forms : ", forms);

        /** Sauvegarde des formulaires sans les Dashboards */
        let toSaveForms = forms.filter(form => form.IsUpdatesTracked)

        console.debug("ObjectStore - Save - toSaveForms : ", toSaveForms);

        //#region Pour chaque champ, on vérifie s'il est requis
        // Pour chaque champ, on vérifie s'il est requis
        toSaveForms.forEach(fo => fo.Sections.forEach(s => {
            /** S'il s'agit d'un tableau de IFields (et non d'un dictionnaire ==> Pour Affectations) */
            if (Array.isArray(s.Fields)) {
                s.Fields.forEach(f => {
                    if (f.Type === FieldTypeEnum.Number && 'Value' in f && f.Value) {
                        let field: NumberField = (f as NumberField);
                        console.debug("ObjectStore - Save() - NumberField.Name : %O - field.Value : %O - field.MinValue : %O - field.MaxValue : %O", field.Name, field.Value, field.MinValue, field.MaxValue);
                        if (field.MinValue && (field.Value < field.MinValue)) {
                            f['State'] = false;
                            this.context.commit('setFieldError', { formName: fo.Name, target: fo.Target, fieldName: f['Name'], formLabel: fo.Label, isFullWidth: fo.IsFullWidth, error: "La valeur de '" + field.Label + "' doit être supérieure à " + field.MinValue });
                        }

                        if (field.MaxValue && (field.Value > field.MaxValue)) {
                            f['State'] = false;
                            this.context.commit('setFieldError', { formName: fo.Name, target: fo.Target, fieldName: f['Name'], formLabel: fo.Label, isFullWidth: fo.IsFullWidth, error: "La valeur de '" + field.Label + "' doit être inférieure à " + field.MaxValue });
                        }
                    }

                    if (f.Type === FieldTypeEnum.Date && 'Value' in f && f.Value) {
                        console.debug('ObjectStore - Save() - f.Type === FieldTypeEnum.Date - f.Value : ', f.Value);
                        if (!moment(f.Value, ['DD/MM/YYYY', 'YYYY-MM-DD', 'DD/MM/YYYY 00:00', 'YYYY-MM-DD 00:00', 'DD/MM/YYYYT00:00:00', 'YYYY-MM-DDT00:00:00'], true).isValid()) {
                            f['State'] = false;
                            this.context.commit('setFieldError', { formName: fo.Name, target: fo.Target, fieldName: f['Name'], formLabel: fo.Label, isFullWidth: fo.IsFullWidth, error: "Le champ '" + f['Label'] + "' n'est pas à un format de date valide." });
                        }
                    }

                    if ('Required' in f && f.Required && ('Value' in f && !f.Value ||
                        (f.Type === FieldTypeEnum.Text && ('Value' in f && f.Value as string).trim() === '')
                        || (Array.isArray(f.Value) && f.Value.length == 0))) {
                        // Change l'état du champ pour qu'il apparaisse avec un encadré rouge dans le formulaire si le champ est requis
                        f['State'] = false;
                        this.context.commit('setFieldError', { formName: fo.Name, target: fo.Target, fieldName: f['Name'], formLabel: fo.Label, isFullWidth: fo.IsFullWidth, error: "Le champ '" + f['Label'] + "' est requis." });
                    }
                });
            } else {
                console.debug("ObjectStore - Save - s : ", s);

                // Test les noeuds d'affectations s'il comporte tous des pourcentages supérieur à 0
                if ('Node' in s.Fields) {
                    s.Fields['Node'].forEach(f => {
                        /** La Condition f.Column != 1 test s'il s'agit d'autre chose que d'un sous-compteur (qui sont uniquement en colonne 1 des affectations) */
                        if (f.Type == FieldTypeEnum.Node && 'Value' in f && f.Value && f.Column != 1 && f['Value']?.Percentage <= 0) {
                            f['State'] = false;
                            this.context.commit('setFieldError', { formName: fo.Name, target: fo.Target, fieldName: fo['Id'], formLabel: fo.Label, isFullWidth: fo.IsFullWidth, error: "Une répartition comporte une valeur à 0." });
                        }
                    });
                }
            }
        }));

        console.debug("ObjectStore - Save() - Object.keys(this.formsErrors).length : %O", Object.keys(this.formsErrors)?.length);

        // Si des erreurs de validations ont été trouvées, on sort du Save()
        if (Object.keys(this.formsErrors).length > 0)
            throw(new Error("Des champs sont invalides !"));
        //#endregion Pour chaque champ, on vérifie s'il est requis

        //#region Constitution de la liste des Fields à sauvegarder côté server
        // Constitue la liste des Fields à sauvegarder côté server
        /** @todo: ⚠ gestion Contacts et Allocation dans save à rendre plus générique.... pas de spécifité à mettre pour un formulaire ici !!! ⚠  */
        const values: Dictionary<any> = {};

        let allocations: Dictionary<any>[] = [];

        (toSaveForms as FormDraft[]).forEach(f => (f.Sections as Section[])
            .forEach(s => {
                if (f.Target == "Allocation") {  /** @todo Voir quoi renvoyer pour les affectations avec SLavina */
                    values["Allocation"] = f;

                    let allocSection: Section<Period, Dictionary<IField[]>> = (s as any as Section<Period, Dictionary<IField[]>>);

                    let allocation: Dictionary<any> = {};
                    allocation['Type'] = allocSection.Name;
                    allocation['StartDate'] = allocSection.Value.StartDate;
                    allocation['EndDate'] = allocSection.Value.EndDate;
                    allocation['GraphNodes'] = allocSection.Fields['Node'];
                    allocation['GraphEdges'] = allocSection.Fields['Edge'];

                    allocations.push(allocation);
                }
                else if (f.Target === "Contacts") {  /** Gestion spécifique du formulaire Contact pour n'envoyer que les données nécessaires (TypedContactStruct côté server) et non tout le type Contact */
                    let contacts: { ContactId: string, Type: Number }[] = [];

                    (s.Fields as FormField[]).forEach(f => f['Value'].forEach((v: Contact) => {
                        contacts.push({ ContactId: v.Id, Type: (v.Type ? v.Type : 0) });
                    }));
                    values["Contacts"] = contacts;
                }
                /** S'il s'agit d'un tableau de IFields (et non d'un dictionnaire ==> Pour Affectations) */
                else if (Array.isArray(s.Fields)) {
                    console.debug("ObjectStore - save() - section : %s - fields (isArray) : %O", s.Label, s.Fields);

                    (s.Fields as FormField[]).forEach(f => {
                        values[f.Name] = f['Value'];
                    })
                }
                else {  // S'il s'agit d'un dictionnaire (un objet classique en TypeScript...)
                    console.debug("ObjectStore - save() - section : %s - fields (else) : %O", s.Label, s.Fields);

                    // values[s.Name] = s.Fields

                    let innerValues: Dictionary<any> = {};

                    for (let field in (s.Fields as Dictionary<IField[]>)) {
                        console.debug("ObjectStore - save() - section : %s - for field (else) : %O - s.Fields[field] : %O", s.Label, field, s.Fields[field]);
                        // innerValues[field['Name']] = field['Value'];

                        if ((s.Fields[field]['Value'] && Array.isArray(s.Fields[field]['Value'] && (s.Fields[field]['Value'] as any[]).length > 0) ) || (s.Fields[field]['Value'] && Object.keys(s.Fields[field]['Value']).length > 0))
                            innerValues[field] =  s.Fields[field]['Value'];
                    }

                    values[s.Name] = innerValues;
                }
            })
        );

        console.debug("ObjectStore - save() - values : %O", values);

        if (allocations)
            values['Allocation'] = allocations;

        // Constitue la liste des Fields à sauvegarder côté server pour les sous-formulaires
        (toSaveForms as FormDraft[]).forEach(f => (f.SubForms as SubFormDraft[])
            .forEach(sub => {
                // Liste des valeurs pour le sous-formulaire
                let subValues: Dictionary<any> = {};
                (sub.Sections as Section[]).forEach(s => (s.Fields as FormField[]).forEach(fi => {
                    subValues[fi.Name] = fi['Value'];
                }))

                values[sub.Name] = [sub.Mode, subValues];
            })
        );
        //#endregion Constitution de la liste des Fields à sauvegarder côté server

        let currentSaveMode: SaveModeEnum | undefined = (localSaveMode ? localSaveMode : this.saveMode);

        /** Paramètres passés à l'API Save */
        console.debug("ObjectStore - Save() - toSaveForms : %O - values - :%O", toSaveForms, values);
        const args: Dictionary<any> = {};
        args["Token"] = Vue.cookies.get('access_token') != null ? <string>Vue.cookies.get('access_token') : "";
        args["VersionId"] = this.versionId;
        args["Values"] = JSON.stringify(values);
        args["SaveMode"] = currentSaveMode;
        if (versionDate)
            args["VersionDate"] = moment(versionDate).format('DD/MM/YYYY');

        /** @hack: Formatage de VersionDate au format long français pour compatilibité avec le parser json utilisé côté server */
        if (currentSaveMode == SaveModeEnum.NewObject)  // S'il s'agit d'un nouvel objet alors on prend le SingleNodeId comme parent
            args["ParentId"] = this.parentNode.Id;
        else
            args["ObjectId"] = this.nodeId;

        // Interroge l'API correspondant au type d'objet du SingleNode sélectionné
        await axios.post(API_URL + "object/" + this.objectType + "/Save", args).then((response) => {
            if (response && response.data) {
                let loadedData: Dictionary<any> = response.data;
                console.debug("ObjectStore - Save() - loadedData : ", loadedData);
                this.context.commit('setData', { versionId: loadedData["VersionId"], isSaveHidden: ("IsSaveHidden" in loadedData ? loadedData["IsSaveHidden"] : false), isVersionsHidden: ("IsVersionsHidden" in loadedData ? loadedData["IsVersionsHidden"] : false), forms: loadedData["Forms"], defaultFormName: loadedData["DefaultFormName"] });
                this.context.commit('setVersionsHistory', loadedData["Revisions"]);
                // Réinitialisation du saveMode
                this.context.commit('setSaveMode', SaveModeEnum.UpdateObject);
                StoreMediator.GetInstance().SaveObject(currentSaveMode, this.GetCurrentVersionId, this.versionId, loadedData["PersistentId"], (toSaveForms[0].Sections[0].Fields[0] as any).Value).then(() => {
                    console.info('ObjectStore - Save() - Enregistrement terminé !');
                });
            }
        });
    }

    @Action({ rawError: true })
    public async Copy({ nodeId }) {
        this.context.commit('copy', nodeId);
    }

    @Action({ rawError: true })
    public async Paste({ destRef }) {
        const args: Dictionary<any> = {};
        args["Token"] = Vue.cookies.get('access_token') != null ? <string>Vue.cookies.get('access_token') : "";
        args["CopyRef"] = this.copiedNodeId;
        args["DestRef"] = destRef;

        await axios.post(API_URL + "object/Paste", args).then((response) => {
            if (response && response.data) {
                let success: boolean = response.data["Success"];

                if (success)
                    StoreMediator.GetInstance().PasteObject(this.copiedNodeId);
                else {
                    let errorMessage: string = response.data["ErrorMessage"];
                    throw(new Error(errorMessage));
                }
            }
        });
    }

    @Action({ rawError: true })
    public async Rename({ nodeId, newName }) {
        const args: Dictionary<any> = {};
        args["Token"] = Vue.cookies.get('access_token') != null ? <string>Vue.cookies.get('access_token') : "";
        args["NodeId"] = nodeId;
        args["NewName"] = newName;

        await axios.post(API_URL + "object/Rename", args).then((response) => {
            if (response && response.data) {
                this.context.commit("rename", { nodeId: nodeId, newName: newName });
                StoreMediator.GetInstance().RenameObject(nodeId, newName);
            }
        });
    }

    @Action({ rawError: true })
    public async SetTempObjectId(): Promise<any> {
        await this.context.commit('setTempObjectId');
    }

    @Action({ rawError: true })
    public async LoadChildrenSection({ objectType }) {
        const args: Dictionary<any> = {};
        args["Token"] = Vue.cookies.get('access_token') != null ? <string>Vue.cookies.get('access_token') : "";
        args["ObjectId"] = this.nodeId; /** @todo : nodeId ou objectId ??!! 🙄🙄🙄🙄 */
        args["ObjectType"] = objectType;
        
        await axios.post(API_URL + "object/LoadChildrenSection", args).then((response) => {
            if (response && response.data) {
                // Mise à jour des tableaux (QUESTION : à chaque fois où bien tester si existe déjà ?)
                let section: Section = (getFormElement(this.forms, objectType) as Section);
                if(section)
                    section.Fields = response.data;
            }
        });
    }

    /** 
	 * Mise à jour d'un widget sur un tableau de bord (Pourquoi utiliser ça dans la fiche objet ?)
	 * @param dashboardId Id du tableau de bord
	 * @param widgetId	Identifiant du widget
	 * @param datas Données du widget à mettre à jour
	 **/
	@Action({ rawError: true })
	public UpdateWidget({ dashboardId, widgetId, datas }) {
		console.debug("ObjectStore - UpdateWidget() - {dashboardId: %O, widgetId: %O, Datas: %O ", dashboardId, widgetId, datas);
		this.context.commit('updateWidget', { dashboardId, widgetId, datas }) ;
	}
//#endregion Actions
//#region Getters
    /** Obtient le NodeId du noeud chargé dans la fiche objet */
    public get GetNodeId(): string {
        return this.nodeId;
    }

    /** Obtient le type du noeud chargé dans la fiche objet en chaine */
    public get GetObjectType(): string {
        return this.objectType;
    }

    public get GetParentNode(): TreeNode {
        return this.parentNode;
    }

    /** Indique si la fiche objet doit être rechargé */
    public get GetRefreshKey(): number {
        return this.refreshKey;
    }

    /** Indique le nom du noeud chargé dans la fiche objet */
    public get GetNodeName(): string {
        let nodeName: TextField = (getFormElement(this.forms, "Name", "Field") as TextField);
        if (nodeName)
            return nodeName.Value;
    }

    /** Renvoi toute la liste de formulaires pour l'objet courant */
    public get GetForms(): FormDraft[] {
        return this.forms;
    }

    /** Renvoi toute la liste des historiques de versions pour l'objet courant */
    public get GetVersionsHistory(): VersionHistory[] {
        return this.versionsHistory;
    }

    /** Indique si le bouton de sauvegarde est masqué ou non */
    public get GetIsSaveHidden(): boolean {
        return this.isSaveHidden;
    }

    /** Indique si l'historique des versions doit être affiché ou non pour l'objet */
    public get GetIsVersionsHidden(): boolean {
        return this.isVersionsHidden;
    }

    /** Renvoi l'Id de version sélectionnée */
    public get GetVersionId(): number {
        return this.versionId;
    }

    /** Renvoi le formulaire par défaut de la fiche objet */
    public get GetDefaultFormName(): string {
        return this.defaultFormName;
    }

    /** Renvoi le dernier Id de version correspondant à la version courante */
    public get GetCurrentVersionId(): number {
        if (this.versionsHistory)
            return Math.max(...this.versionsHistory.map(item => item.Version));
        else
            return undefined;
    }


    /** Retourne l'Id de noeud copié */
    public get CopiedNodeId(): string {
        return this.copiedNodeId;
    }

    /** Renvoi le dernier ObjectId temporaire disponible */
    public get GetTempObjectId(): number {
        return this.tempObjectId;
    }

    /** Renvoi une erreur associée à un champ */
    public get GetFieldError() {
        return (formName: string, fieldName: string): string => {
            if (formName in this.formsErrors)
                return this.formsErrors[formName]['FieldsErrors'][fieldName];
        }
    }

    /** Renvoi l'ensemble du dictionnaire d'erreurs */
    public get GetFormsErrors(): Dictionary<{ Target: string, FormLabel: string, IsFullWidth: boolean, FieldsErrors: Dictionary<string> }> {
        return this.formsErrors;
    }

    /** 
     * 	Renvoi la date de la version sélectionnée
     *  Un texte est affiché à la place de la version courante
     */
    public get GetFormatedVersionDate(): string {
        console.debug("GetFormatedVersionDate - this.versionsHistory.length : %O - this.versionId : %O", this.versionsHistory?.length, this.versionId);

        // Si l'historique n'a pas encore été chargé alors on considère qu'il s'agit de la version en cours. Soit "Aujourd'hui"
        if (!this.versionsHistory || (this.versionsHistory.length == 0 && this.versionId > 0))
            return i18n.t('Today').toString();   // "Aujourd'hui"

        let versionIdF: number = this.versionsHistory.findIndex(x => x.Version === this.versionId);

        // S'il s'agit de la version courante
        if (this.versionId == this.GetCurrentVersionId)
            return i18n.t('Today').toString();   // "Aujourd'hui"
        else
            if (versionIdF > -1)
                return moment(String(this.versionsHistory[versionIdF].EndDate)).format('L');
            else
                return "";
    }

    /** Mode de sauvegarde à utiliser sur Save() lorsqu'il doit être défini au préalable (sur New/création d'un objet par exemple) */
    public get GetSaveMode(): SaveModeEnum | null {
        return this.saveMode;
    }

    /** Indique si des formulaires de la fiche objet comporte des champs mis à jour */
    public get GetUpdatedForms(): string[] {
        return this.updatedForms;
    }

    /** Recherche un élement de formulaire (form, section ou field)
     * @param name  Nom de l'élement à rechercher
     * @param type  Peut contenir "Form", "Section" ou "Field". Recherche dans tout type si undefined
     */
    public get GetFormElement() {
        return ({ elementName, type = undefined }): IFormElement => {
            return getFormElement(this.forms, elementName, type);
        }
    }
    //#endregion Getters
}

//#region Fonctions locales
/** Retoune les lignes de TableField correspondant à un nodeId spécifique
 * @param forms Liste des formulaire dans lequel rechercher
 * @param formName Nom de formulaire dans lequel rechercher
 * @param section Nom de la section de formulaire dans laquelle rechercher
 * @param tableFieldName Nom du TableField à rechercher
 * @param nodeId Id de noeud
 * @return dictionnaire de ligne correspondant
 */
function getTableFieldRows(forms: FormDraft[], formName: string = "Children", sectionName: string, tableFieldName: string, nodeId: string): Dictionary<any> {
    let rowDic: Dictionary<any> = {};

    // Mise-à-jour du noeud dans tableau enfants chargés dans forms
    let idChildForm: number = forms.findIndex(x => x.Name == formName);
    if (idChildForm > -1) {
        let childForm: FormDraft = forms[idChildForm];

        if (sectionName && tableFieldName) {
            // Recherche dans la section et le tableField spécifié
            let idSection: number = childForm.Sections.findIndex(x => x.Name == sectionName);
            if (idSection > -1) {
                let section: ISection = childForm.Sections[idSection] as ISection;
                let idTableField: number = section.Fields.findIndex(x => (x as TableField).Name == tableFieldName);
                if (idTableField > -1) {
                    let tableField: TableField = (section.Fields[idTableField] as TableField);
                    // Recherche de la ligne à déprécier
                    let idRow: number = tableField.Value.findIndex(x => x.nodeId == nodeId);
                    console.debug("ObjectStore - getTableFieldRows - sectionName : %O - tableFieldName : %O - idRow  : %O", sectionName, tableFieldName, idRow);
                    if (idRow > -1) {
                        rowDic["tableRows"] = (tableField as TableField).Value;
                        rowDic["idRow"] = idRow;
                    }
                }
            }
        }
    }
    return rowDic;
}

/** Recherche un élement de formulaire (form, section ou field)
 * @param forms Tableau de formulaires dans lequel on recherche un élement (form, section ou field)
 * @param name  Nom de l'élement à rechercher
 * @param type  Peut contenir "Form", "Section" ou "Field". Recherche dans tout type si undefined
 */
function getFormElement(forms: FormDraft[], name: string, type: string = undefined): IFormElement {
    let element!: IFormElement;

    if (type && type == "Form") {
        let idForms: number = forms.findIndex(x => x.Name == name);
        if (idForms > -1)
            element = forms[idForms];
    }
    else {
        for (let form of forms) {
            if (form.Sections && form.Sections.length > 0) {

                if (!type || (type && type == "Section")) {
                    let idSections: number = form.Sections.findIndex(x => x.Name == name);
                    if (idSections > -1) {
                        element = form.Sections[idSections];
                        break;
                    }
                }
                
                if (!type || (type && type == "Field")) {
                    for (let section of form.Sections) {
                        if (section.Fields) {
                            // S'il s'agit d'un tableau de Field
                            if (Array.isArray(section.Fields) && section.Fields.length > 0) {
                                let idField: number = section.Fields.findIndex(x => x['Name'] == name);
                                if (idField > -1) {
                                    element = (section.Fields[idField] as any as IFormElement);
                                    break;
                                }
                            }
                            else if (name in section.Fields) {   // S'il s'agit d'un dictionnaire de Field
                                element = (section.Fields[name] as any as IFormElement);
                                break;
                            }
                        }
                    }
                }
            }
        }
    }
    console.debug("ObjectStore - getFormElement - element : %O ", element);
    return element;
}
//#endregion Fonctions locales

export default ObjectStore;